import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    fetchSingleJob,
    submitJobApplication,
    fetchUserApplications,
    selectSingleJob,
    selectUserApplications,
    selectFetchSingleJobStatus,
    selectSubmitApplicationStatus,
    selectFetchApplicationsStatus,
    selectError,
    clearSingleJob,
    clearApplicationSubmission
} from '../_store/jobpostingsSlice';
import Spinner from 'react-bootstrap/Spinner';
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";

const ApplyJobs = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const singleJob = useSelector(selectSingleJob);
    const userApplications = useSelector(selectUserApplications);
    const fetchStatus = useSelector(selectFetchSingleJobStatus);
    const submitStatus = useSelector(selectSubmitApplicationStatus);
    const applicationsStatus = useSelector(selectFetchApplicationsStatus);
    const error = useSelector(selectError);
    
    const [hasApplied, setHasApplied] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    const pendingJobId = localStorage.getItem('pendingJobId');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = userData._id || 
               userData.id || 
               userData.user?._id || 
               userData.user?.id || 
               userData.profile?._id || 
               userData.profile?.id ||
               userData.sub; 
    
    // Fetch user's applications
    useEffect(() => {
        if (userId) {
            dispatch(fetchUserApplications(userId));
        }
    }, [dispatch, userId]);
    
    // Fetch pending job if exists
    useEffect(() => {
        if (pendingJobId) {
            dispatch(fetchSingleJob({ 
                jobId: pendingJobId, 
                userId: userId 
            }));
        }
        
        return () => {
            dispatch(clearSingleJob());
            dispatch(clearApplicationSubmission());
        };
    }, [dispatch, pendingJobId, userId]);
    
    // Check if pending job is already applied
    useEffect(() => {
        if (singleJob?.has_applied) {
            setHasApplied(true);
        }
    }, [singleJob]);
    
    // Handle successful submission - Show success message briefly then refresh applications
    useEffect(() => {
        if (submitStatus === 'succeeded') {
            // Set success message
            setSuccessMessage('Application submitted successfully!');
            setShowSuccess(true);
            setHasApplied(true);
            
            // Remove pending job from localStorage
            localStorage.removeItem('pendingJobId');
            
            // Refresh applications list immediately
            if (userId) {
                dispatch(fetchUserApplications(userId));
            }
            
            // Clear the pending job from the view
            dispatch(clearSingleJob());
            dispatch(clearApplicationSubmission());
            
            // Auto-hide success message after 10 seconds
            const timer = setTimeout(() => {
                setShowSuccess(false);
                setSuccessMessage('');
            }, 10000);
            
            return () => clearTimeout(timer);
        }
    }, [submitStatus, dispatch, userId]);
    
    const handleCompleteApplication = () => {
        if (!userId) {
            alert('User not found. Please login again.');
            navigate('/account/login');
            return;
        }
        
        dispatch(submitJobApplication({ 
            jobId: pendingJobId, 
            userId: userId 
        }));
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };
    
    const styles = {
        container: {
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: '2rem'
        },
        headerContainer: {
            textAlign: 'center',
            marginBottom: '2rem'
        },
        header: {
            margin: 0,
            fontSize: '2rem',
            color: '#333'
        },
        subheader: {
            color: '#666',
            fontSize: '1rem',
            marginTop: '0.5rem'
        },
        backButton: {
            background: '#6c757d',
            border: 'none',
            color: 'white',
            fontSize: '0.9rem',
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            marginBottom: '2rem'
        },
        card: {
            maxWidth: '800px',
            margin: '0 auto',
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        },
        jobTitle: {
            fontSize: '1.75rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
        },
        jobCompany: {
            fontSize: '1.2rem',
            color: '#007bff',
            marginBottom: '1rem'
        },
        jobLocation: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#666',
            fontSize: '1rem',
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: '8px'
        },
        section: {
            marginBottom: '1.5rem'
        },
        label: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '0.5rem',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        description: {
            color: '#333',
            lineHeight: '1.8',
            fontSize: '1rem',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
        },
        meta: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #eee',
            color: '#999',
            fontSize: '0.9rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginTop: '2rem',
            justifyContent: 'flex-end'
        },
        applyButton: {
            background: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            transition: 'background 0.2s'
        },
        cancelButton: {
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            transition: 'background 0.2s'
        },
        disabledButton: {
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '500',
            opacity: 0.6,
            cursor: 'not-allowed'
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '3rem',
            color: '#666',
            fontSize: '1.2rem'
        },
        errorContainer: {
            maxWidth: '600px',
            margin: '2rem auto',
            padding: '2rem',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            textAlign: 'center'
        },
        successToast: {
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#28a745',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            animation: 'slideIn 0.3s ease-out'
        },
        userInfo: {
            background: '#e7f3ff',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #b8daff'
        },
        userInfoText: {
            margin: '0.25rem 0',
            color: '#004085'
        },
        applicationsSection: {
            maxWidth: '800px',
            margin: '2rem auto 0 auto'
        },
        sectionTitle: {
            fontSize: '1.5rem',
            color: '#333',
            marginBottom: '1rem'
        },
        applicationCard: {
            background: 'white',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #e9ecef',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
        },
        applicationHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
        },
        applicationTitle: {
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#333',
            margin: 0
        },
        statusBadge: {
            padding: '0.25rem 0.75rem',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            fontSize: '0.8rem',
            fontWeight: '500'
        },
        applicationMeta: {
            color: '#666',
            fontSize: '0.9rem',
            marginBottom: '0.25rem'
        },
        pendingBanner: {
            background: '#fff3cd',
            color: '#856404',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #ffeeba',
            textAlign: 'center'
        },
        noApplicationsCard: {
            textAlign: 'center',
            padding: '3rem',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        },
        browseButton: {
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            marginTop: '1rem'
        }
    };
    
    // Loading State
    if ((pendingJobId && fetchStatus === 'loading') || applicationsStatus === 'loading') {
        return (
            <div style={styles.container}>
                <div style={styles.headerContainer}>
                    <h1 style={styles.header}>My Applications</h1>
                    <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '150px', marginTop: '1rem' }} />
                </div>
                <div style={styles.loadingContainer}>
                    <Spinner animation="border" />
                    <p style={{ marginTop: '1rem' }}>Loading your applications...</p>
                </div>
            </div>
        );
    }
    
    // Error State
    if (fetchStatus === 'failed' && pendingJobId) {
        return (
            <div style={styles.container}>
                <div style={styles.headerContainer}>
                    <h1 style={styles.header}>My Applications</h1>
                    <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '150px', marginTop: '1rem' }} />
                </div>
                <div style={styles.errorContainer}>
                    <h3>Error Loading Job</h3>
                    <p>{error || 'Failed to load job details.'}</p>
                    <button 
                        onClick={() => navigate('/home')}
                        style={{...styles.cancelButton, marginTop: '1rem'}}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div style={styles.container}>
            {/* Success Toast Notification */}
            {showSuccess && (
                <div style={styles.successToast}>
                    <strong>✓ {successMessage}</strong>
                </div>
            )}
            
            <div style={styles.headerContainer}>
                <h1 style={styles.header}>My Applications</h1>
                <p style={styles.subheader}>View and manage your projects applications</p>
                <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '150px', marginTop: '1rem' }} />
            </div>
            
            <button 
                style={styles.backButton}
                onClick={() => navigate('/home')}
                onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
            >
                ← Back to Dashboard
            </button>
            
            {/* User Info Banner */}
            <div style={styles.userInfo}>
                <p style={styles.userInfoText}>
                    <strong>Logged in as:</strong> {userData.firstname || userData.name || userData.email || 'User'}
                </p>
                <p style={styles.userInfoText}>
                    <strong>Email:</strong> {userData.email || 'N/A'}
                </p>
            </div>
            
            {/* Pending Job Banner - Only show if pendingJobId exists and not applied */}
            {pendingJobId && !hasApplied && (
                <div style={styles.pendingBanner}>
                    You have a pending application. Complete it below!
                </div>
            )}
            
            {/* Pending Job Card - Show if pendingJobId exists and not applied */}
            {pendingJobId && singleJob && !hasApplied && (
                <div style={styles.card}>
                    <h2 style={styles.jobTitle}>
                        {singleJob?.name || singleJob?.title || 'Untitled Position'}
                    </h2>
                    
                    <div style={styles.jobLocation}>
                        <span><strong>Location:</strong> {singleJob?.location || 'Not Specified'}</span>
                    </div>
                    
                    <div style={styles.section}>
                        <div style={styles.label}>Job Description</div>
                        <div style={styles.description}>
                            {singleJob?.enhancedDescription || singleJob?.originalDescription || singleJob?.description || 'No description available'}
                        </div>
                    </div>
                    
                    <div style={styles.meta}>
                        <span>Posted: {formatDate(singleJob?.createdAt || singleJob?.posted_at)}</span>
                    </div>
                    
                    <div style={styles.buttonGroup}>
                        <button 
                            style={styles.applyButton}
                            onClick={handleCompleteApplication}
                            disabled={submitStatus === 'loading'}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#218838')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#28a745')}
                        >
                            {submitStatus === 'loading' ? (
                                <>
                                    <Spinner animation="border" size="sm" /> Submitting...
                                </>
                            ) : (
                                'Complete Application'
                            )}
                        </button>
                    </div>
                </div>
            )}
            
            {/* All Applications Section */}
            <div style={styles.applicationsSection}>
                <h3 style={styles.sectionTitle}>
                    {userApplications.length > 0 ? `Applied Jobs (${userApplications.length})` : 'No Applications Yet'}
                </h3>
                
                {userApplications.length === 0 ? (
                    <div style={styles.noApplicationsCard}>
                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                            You haven't applied to any project yet. Browse project openings to get started!
                        </p>
                        <button 
                            style={styles.browseButton}
                            onClick={() => navigate('/jobopenings')}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#0056b3'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#007bff'}
                        >
                            Browse Available Projects
                        </button>
                    </div>
                ) : (
                    userApplications.map((app, index) => (
                        <div 
                            key={app._id || index} 
                            style={styles.applicationCard}
                            onClick={() => navigate(`/job-details/${app.job_id}`)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                            }}
                        >
                            <div style={styles.applicationHeader}>
                                <h4 style={styles.applicationTitle}>
                                    {app.job_title || 'Untitled Position'}
                                </h4>
                                <span style={styles.statusBadge}>
                                    {app.status || 'Applied'}
                                </span>
                            </div>
                            <p style={styles.applicationMeta}>
                                <strong>Location:</strong> {app.job_location || 'N/A'}
                            </p>
                            <p style={styles.applicationMeta}>
                                <strong>Applied:</strong> {formatDate(app.applied_at)}
                            </p>
                            <p style={{...styles.applicationMeta, fontSize: '0.8rem', color: '#999'}}>
                                Application ID: {app._id?.slice(-8) || 'N/A'}
                            </p>
                        </div>
                    ))
                )}
            </div>
            
            {/* Add CSS animation for toast */}
            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default ApplyJobs;
